// Counts practice time on the song page and keeps the practice log (see
// practice-api) up to date. Time counts while one of the dock's tools is
// going (metronome, backing track, following, chord-change trainer) or
// while the player's own practice timer runs and the page is in view.

import { useCallback, useEffect, useRef, useState } from 'react';
import { track, trackOnce } from '../../usage';
import { fetchSummary, logTime, setLearning } from './practice-api';

// Send the time every so often rather than every second.
const SEND_EVERY = 60;
// Less than this isn't worth a request (it waits for the next one instead).
const MIN_SEND = 5;

export default function usePracticeLog({ songId, isAuthenticated, practising }) {
    const [timerOn, setTimerOn] = useState(false);
    const [visible, setVisible] = useState(() => !document.hidden);
    // Time on this song since the page was opened (or the song changed).
    const [sessionSeconds, setSessionSeconds] = useState(0);
    const [summary, setSummary] = useState(null);
    const [failed, setFailed] = useState(false);
    const pending = useRef(0);
    const songRef = useRef(songId);
    const authRef = useRef(isAuthenticated);
    authRef.current = isAuthenticated;

    const counting = practising || (timerOn && visible);

    const send = useCallback(({ keepalive = false } = {}) => {
        const seconds = pending.current;
        if (!authRef.current || seconds < MIN_SEND || !songRef.current) return;
        pending.current = 0;
        const song = songRef.current;
        logTime(song, seconds, { keepalive })
            .then(result => {
                setSummary(current => (current ? { ...current, ...result, song: song === songRef.current ? result.song : current.song } : current));
            })
            .catch(() => {
                // Try again with the next lot.
                pending.current += seconds;
            });
    }, []);

    useEffect(() => {
        const onVisibility = () => setVisible(!document.hidden);
        const onLeave = () => send({ keepalive: true });
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', onLeave);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', onLeave);
            send({ keepalive: true });
        };
    }, [send]);

    useEffect(() => {
        if (!counting) return undefined;
        const timer = setInterval(() => {
            pending.current += 1;
            setSessionSeconds(s => s + 1);
            if (pending.current >= SEND_EVERY) send();
        }, 1000);
        return () => {
            clearInterval(timer);
            send();
        };
    }, [counting, send]);

    // Another song: what was practised goes to the one before.
    useEffect(() => {
        if (songRef.current !== songId) {
            send();
            songRef.current = songId;
            setSessionSeconds(0);
            setSummary(current => (current ? { ...current, song: null } : current));
        }
    }, [songId, send]);

    const refresh = useCallback(() => {
        if (!authRef.current) return;
        send();
        fetchSummary(songRef.current)
            .then(result => {
                setSummary(result);
                setFailed(false);
            })
            .catch(() => setFailed(true));
    }, [send]);

    const changeLearning = status => {
        if (!authRef.current) return;
        const song = songRef.current;
        setLearning(song, status)
            .then(result => setSummary(current => ({ ...(current || {}), ...result })))
            .catch(() => setFailed(true));
        track('learning_status_set', { status: status || 'removed' });
    };

    return {
        counting,
        timerOn,
        startTimer: () => {
            trackOnce('practice_timer_started');
            setTimerOn(true);
        },
        stopTimer: () => setTimerOn(false),
        sessionSeconds,
        // Counted here but not sent yet.
        unsent: () => pending.current,
        summary,
        failed,
        refresh,
        changeLearning,
    };
}
